import { api, ApiError } from "./http";

/** Browser alerts: Web Push through the service worker (works with the tab closed) plus the plain Notification
 *  API while the tab is open but hidden. The server decides who gets what (mute, quiet hours, smart mute). */

export type PushStatus = "unsupported" | "blocked" | "off" | "on" | "unconfigured";

const SUB_ID_KEY = "spurt.push.id";

export const pushSupported = (): boolean =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const store = {
  get: (): string => {
    try {
      return window.localStorage.getItem(SUB_ID_KEY) ?? "";
    } catch {
      return "";
    }
  },
  set: (v: string) => {
    try {
      if (v) window.localStorage.setItem(SUB_ID_KEY, v);
      else window.localStorage.removeItem(SUB_ID_KEY);
    } catch {
      /* not remembered */
    }
  },
};

export const urlBase64ToUint8Array = (b64: string): Uint8Array<ArrayBuffer> => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("service worker registration failed", e);
    return null;
  }
};

const currentSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
};

export const pushStatus = async (): Promise<PushStatus> => {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const sub = await currentSubscription().catch(() => null);
  return sub && Notification.permission === "granted" ? "on" : "off";
};

const sendToServer = async (sub: PushSubscription): Promise<void> => {
  const j = sub.toJSON();
  const keys = j.keys ?? {};
  const r = await api<{ id: string }>("/api/v1/notifications/webpush/subscriptions", {
    method: "POST",
    body: { endpoint: sub.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth }, userAgent: navigator.userAgent.slice(0, 200) },
  });
  store.set(r.id);
};

/** Must be called from a click: browsers only show the permission prompt after a user gesture. */
export const enablePush = async (): Promise<PushStatus> => {
  if (!pushSupported()) return "unsupported";
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "blocked" : "off";
  let key: string;
  try {
    key = (await api<{ publicKey: string }>("/api/v1/notifications/webpush/key")).publicKey;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 501 || e.status === 404)) return "unconfigured";
    throw e;
  }
  const reg = (await navigator.serviceWorker.getRegistration("/")) ?? (await registerServiceWorker());
  if (!reg) return "unsupported";
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  await sendToServer(sub);
  return "on";
};

export const disablePush = async (): Promise<void> => {
  const id = store.get();
  const sub = await currentSubscription().catch(() => null);
  if (id) await api(`/api/v1/notifications/webpush/subscriptions/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(() => undefined);
  if (sub) await sub.unsubscribe().catch(() => undefined);
  store.set("");
};

/** On every start: if the user already agreed, re-register the subscription. The server upserts by endpoint, so this is
 *  harmless, and it also moves a shared browser's subscription to whoever is signed in now. */
export const refreshPushRegistration = async (): Promise<void> => {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    const sub = await currentSubscription();
    if (sub) await sendToServer(sub);
  } catch {
    /* push may not be configured on the server; nothing to do */
  }
};

/** Tab is open but hidden: show a normal notification. Same `tag` as the push, so the two collapse into one. */
export const showLocalNotification = (n: { title: string; body?: string | undefined; tag: string; link?: string | undefined; urgent?: boolean }): void => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const x = new Notification(n.title, { body: n.body ?? "", tag: n.tag, icon: "/favicon.svg", requireInteraction: !!n.urgent });
    x.onclick = () => {
      window.focus();
      window.dispatchEvent(new CustomEvent("spurt:navigate", { detail: { link: n.link ?? "/" } }));
      x.close();
    };
  } catch {
    /* some mobile browsers only allow notifications from the service worker */
  }
};

/** Tab title and app-icon badge. */
export const applyBadge = (count: number, appName = "Spurt"): void => {
  document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${appName}` : appName;
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => undefined);
    else void nav.clearAppBadge?.()?.catch(() => undefined);
  } catch {
    /* unsupported */
  }
};

/** Deep link sent by the service worker or a local notification → "/chat/<convId>" etc. */
export const onNavigate = (fn: (link: string) => void): (() => void) => {
  const fromWindow = (e: Event) => fn(String((e as CustomEvent<{ link?: string }>).detail?.link ?? "/"));
  const fromSw = (e: MessageEvent) => {
    const d = e.data as { type?: string; link?: string } | undefined;
    if (d?.type === "navigate") fn(d.link ?? "/");
  };
  window.addEventListener("spurt:navigate", fromWindow);
  navigator.serviceWorker?.addEventListener("message", fromSw);
  return () => {
    window.removeEventListener("spurt:navigate", fromWindow);
    navigator.serviceWorker?.removeEventListener("message", fromSw);
  };
};
